/**
 * Tipos de dominio del NOC (US 2.6) — Admin Web Go Electric.
 *
 * Módulo único de exportación: la forma exacta de los endpoints reales del backend
 * (`GET /api/v1/noc/*`, SSE `/api/v1/noc/stream`, simulación `/api/v1/noc/sim/*`).
 * Contrato espejo de `services/backend/src/backoffice-crm/noc/interfaces/noc.interface.ts`,
 * `.../noc/alerts/noc-alert.types.ts`, `.../noc/sim/*` y `services/backend/src/live/*`,
 * y del tag `noc` de `services/backend/openapi.yaml`.
 *
 * Aquí solo vive el contrato de datos (sin lógica de red ni de UI). El semáforo
 * (color + icono + texto) pertenece a `@goelectric/ui-components`.
 */

/* ========================================================================= */
/* Estaciones y conectores                                                    */
/* ========================================================================= */

/** Estados granulares del cargador (6) que expone el backend. */
export const NOC_STATION_STATES = [
  'AVAILABLE',
  'CHARGING',
  'OCCUPIED',
  'FAULTED',
  'OFFLINE',
  'MAINTENANCE',
] as const;
export type NocStationState = (typeof NOC_STATION_STATES)[number];

/** Semáforo compacto del mapa: Verde (operativo) / Rojo (falla) / Gris (sin conexión). */
export const NOC_SEMAPHORES = ['VERDE', 'ROJO', 'GRIS'] as const;
export type NocSemaphore = (typeof NOC_SEMAPHORES)[number];

/** Estado de conector (5 canónicos OCPP 2.0.1 + UNKNOWN cuando no hay dato). */
export const NOC_CONNECTOR_STATES = [
  'AVAILABLE',
  'OCCUPIED',
  'RESERVED',
  'UNAVAILABLE',
  'FAULTED',
  'UNKNOWN',
] as const;
export type NocConnectorState = (typeof NOC_CONNECTOR_STATES)[number];

/** Vitalidad calculada sobre `last_seen_at` (un latido viejo no es ONLINE). */
export const NOC_LIVENESS = ['ONLINE', 'STALE', 'OFFLINE'] as const;
export type NocLiveness = (typeof NOC_LIVENESS)[number];

export interface NocCoordinates {
  lat: number;
  lng: number;
}

/** Conteo de conectores de una estación por estado. */
export interface NocConnectorCounts {
  total: number;
  byState: Record<NocConnectorState, number>;
}

/** Conector en el detalle de estación (incluye métricas en vivo de MeterValues). */
export interface NocConnectorSummary {
  connectorId: number;
  evseId: number;
  state: NocConnectorState;
  stateRaw: string | null;
  semaphore: NocSemaphore;
  errorCode: string | null;
  lastSeenAt: string | null;
  /** Métricas en vivo (MeterValues → Redis). `null` = sin muestra reciente. */
  powerKw: number | null;
  currentA: number | null;
  voltageV: number | null;
  temperatureC: number | null;
  energyWh: number | null;
  soc: number | null;
  /** `true` si las métricas provienen del live-state (Redis). */
  live: boolean;
}

/** Fila del listado de estaciones del NOC (mapa + tarjetas/tabla). */
export interface NocStationSummary {
  stationId: string;
  /** Nombre DEMO (`DEMO — ...`) mientras US 2.2 no aporte locaciones reales. */
  name: string | null;
  city: string | null;
  coordinates: NocCoordinates | null;
  protocol: string | null;
  vendor: string | null;
  model: string | null;
  /**
   * Estado de registro real de la estación (respuesta `BootNotification`, TG-75).
   * Prefiere el valor durable de `station_last_state`; en el detalle, si aún es
   * `null`, cae al `status` de la Data API del core (`boot.status`).
   */
  registrationStatus: string | null;
  state: NocStationState;
  semaphore: NocSemaphore;
  liveness: NocLiveness;
  lastSeenAt: string | null;
  connectors: NocConnectorCounts;
  activeSessions: number;
  /** Potencia instantánea agregada de la estación (kW); `null` si no hay muestra. */
  powerKw: number | null;
}

/** Snapshot de la Data API del core (`/data/configuration/boot`). */
export interface NocBoot {
  status: string | null;
  lastBootTime: string | null;
  heartbeatInterval: number | null;
  source: 'core';
}

/** Sesión de carga activa (transacción del core con `isActive=true`). */
export interface NocActiveSession {
  transactionId: string;
  stationId: string;
  stationName: string | null;
  city: string | null;
  connectorId: number | null;
  evseId: number | null;
  chargingState: string | null;
  energyKwh: number | null;
  /** Potencia instantánea (kW) del live-state; `null` = sin muestra reciente. */
  instantPowerKw: number | null;
  startTime: string | null;
}

/** Detalle de estación: resumen + conectores + boot + sesiones activas. */
export interface NocStationDetail extends NocStationSummary {
  connectorDetails: NocConnectorSummary[];
  boot: NocBoot | null;
  sessions: NocActiveSession[];
}

/** KPIs agregados del dashboard NOC. */
export interface NocOverview {
  generatedAt: string;
  stations: {
    total: number;
    online: number;
    stale: number;
    offline: number;
    byState: Record<NocStationState, number>;
  };
  connectors: {
    total: number;
    byState: Record<NocConnectorState, number>;
  };
  sessions: {
    active: number;
  };
  power: {
    totalInstantKw: number;
    estimated: boolean;
    source: string;
  };
  energy: {
    todayKwh: number;
    currency: 'COP';
    source: string;
  };
}

export interface NocStationsResponse {
  generatedAt: string;
  data: NocStationSummary[];
}

export interface NocActiveSessionsResponse {
  generatedAt: string;
  data: NocActiveSession[];
}

/* ========================================================================= */
/* Estado en vivo (Redis)                                                     */
/* ========================================================================= */

/** Métricas instantáneas de un conector (derivadas de MeterValues). */
export interface MeterMetrics {
  powerKw?: number;
  currentA?: number;
  voltageV?: number;
  temperatureC?: number;
  energyWh?: number;
  soc?: number;
}

export interface LiveConnectorState extends MeterMetrics {
  stationId: string;
  tenantId: number;
  evseId: number;
  connectorId: number;
  status?: string;
  statusRaw?: string;
  errorCode?: string;
  updatedAt?: string;
}

export interface LiveStationState {
  stationId: string;
  tenantId: number;
  liveness?: NocLiveness;
  protocol?: string;
  vendor?: string;
  model?: string;
  lastSeenAt?: string;
  updatedAt?: string;
}

export interface LiveSnapshot {
  stations: LiveStationState[];
  connectors: LiveConnectorState[];
}

/** Snapshot inicial del SSE (`event: snapshot`). */
export interface NocSnapshot {
  generatedAt: string;
  stations: NocStationSummary[];
  live: LiveSnapshot;
}

/* ========================================================================= */
/* Alertas (TG-93)                                                            */
/* ========================================================================= */

export const NOC_ALERT_TYPES = [
  'FAULTED',
  'CONNECTOR_UNAVAILABLE',
  'STATION_OFFLINE',
  'OVER_TEMPERATURE',
] as const;
export type NocAlertType = (typeof NOC_ALERT_TYPES)[number];

export const NOC_ALERT_SEVERITIES = ['CRITICAL', 'WARNING', 'INFO'] as const;
export type NocAlertSeverity = (typeof NOC_ALERT_SEVERITIES)[number];

export const NOC_ALERT_STATUSES = ['OPEN', 'ACKED', 'RESOLVED'] as const;
export type NocAlertStatus = (typeof NOC_ALERT_STATUSES)[number];

export interface NocAlert {
  id: string;
  tenantId: number;
  stationId: string;
  connectorId: number | null;
  type: NocAlertType;
  severity: NocAlertSeverity;
  status: NocAlertStatus;
  message: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  ackedAt: string | null;
  ackedBy: string | null;
}

export interface NocAlertsResponse {
  generatedAt: string;
  data: NocAlert[];
}

/* ========================================================================= */
/* Stream SSE (TG-94, ADR 0027)                                              */
/* ========================================================================= */

export const NOC_STREAM_EVENT_TYPES = [
  'snapshot',
  'station.updated',
  'connector.updated',
  'meter.tick',
  'session.started',
  'session.ended',
  'alert.created',
  'alert.updated',
  'command.sent',
  'command.result',
  'sim.log',
  'sim.progress',
] as const;
export type NocStreamEventType = (typeof NOC_STREAM_EVENT_TYPES)[number];

/**
 * Dirección del flujo OCPP de un evento:
 * - `cs->csms`: uplink (estación → core), p. ej. `StatusNotification`.
 * - `csms->cs`: downlink (core → estación), p. ej. `RemoteStartTransaction`.
 * - `null`: evento derivado del backend (p. ej. barrido de liveness), sin mensaje OCPP.
 */
export type NocStreamDirection = 'cs->csms' | 'csms->cs';

/**
 * Trazabilidad OCPP que el backend añade a los eventos del feed. Campos opcionales
 * para tolerar eventos antiguos o derivados que no provienen de un mensaje OCPP.
 */
export interface NocStreamTrace {
  direction?: NocStreamDirection | null;
  /** Acción OCPP (p. ej. `StatusNotification`, `RemoteStartTransaction`). */
  action?: string | null;
  /**
   * `correlationId` OCPP real cuando el core lo informa. **Nunca** es el `requestId`
   * del backend: si el core no lo trae, queda `null`.
   */
  correlationId?: string | null;
}

/** Motivo que acompaña a los eventos de alerta. */
export type NocAlertEventReason = 'CREATED' | 'REFRESHED' | 'ACKED' | 'RESOLVED';

/** Payload de `alert.created` / `alert.updated`. */
export type NocAlertStreamPayload = NocAlert & { reason: NocAlertEventReason };

/** Payload de `station.updated`. */
export interface NocStationUpdatedPayload extends NocStreamTrace {
  stationId: string;
  tenantId: number;
  liveness?: NocLiveness;
  protocol?: string | null;
  lastSeenAt?: string;
  vendor?: string | null;
  model?: string | null;
}

/** Payload de `connector.updated`. */
export interface NocConnectorUpdatedPayload extends NocStreamTrace {
  stationId: string;
  tenantId: number;
  evseId: number;
  connectorId: number;
  status: NocConnectorState;
  statusRaw: string;
  errorCode: string | null;
}

/** Payload de `meter.tick`. */
export interface NocMeterTickPayload extends MeterMetrics, NocStreamTrace {
  stationId: string;
  tenantId: number;
  evseId: number;
  connectorId: number;
  transactionId: string | null;
  at: string;
}

/** Payload de `session.started` / `session.ended`. */
export interface NocSessionStreamPayload extends NocStreamTrace {
  stationId: string;
  tenantId: number;
  connectorId: number;
  transactionId: string | null;
  idTag?: string | null;
  reason?: string | null;
  protocol?: string | null;
}

/**
 * Payload de `command.sent` — petición downlink que sale del dashboard hacia el EVSE
 * (traza tipo JSON-RPC). Se emite ANTES de tocar el core.
 */
export interface NocCommandSentPayload {
  /** Identificador de la petición downlink generado por el backend. */
  requestId: string;
  stationId: string;
  connectorId: number | null;
  direction: 'csms->cs';
  command: NocSimCommand;
  action: string;
  protocol: string;
  /** Endpoint de la Message API del core, p. ej. `/ocpp/1.6/evdriver/remoteStartTransaction`. */
  endpoint: string;
  at: string;
}

/**
 * Payload de `command.result` — respuesta del core a un comando downlink, correlacionada
 * por `requestId`. `correlationId` es el OCPP real (o `null`); nunca se rellena con el
 * `requestId`.
 */
export interface NocCommandResultPayload extends NocCommandSentPayload {
  correlationId: string | null;
  confirmations: NocCoreMessageConfirmation[];
  /** Mensaje de error si el core rechazó/falló la petición. */
  error?: string;
}

/** Payload de `sim.log`. */
export interface NocSimLogPayload {
  runId: string;
  scenarioId: string;
  level: 'INFO' | 'ERROR';
  message: string;
  at?: string;
}

/** Payload de `sim.progress`. */
export interface NocSimProgressPayload {
  runId: string;
  scenarioId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  step: number;
  totalSteps: number;
  percent: number;
  label?: string;
  error?: string;
}

/* ========================================================================= */
/* Simulación (TG-94, ADR 0029)                                              */
/* ========================================================================= */

export const NOC_SIM_COMMANDS = [
  'remoteStart',
  'remoteStop',
  'reset',
  'unlockConnector',
  'clearCache',
  'triggerMessage',
  'setChargingProfile',
] as const;
export type NocSimCommand = (typeof NOC_SIM_COMMANDS)[number];

/** Comandos destructivos/que interrumpen carga: exigen confirmación en la UI. */
export const NOC_SIM_DESTRUCTIVE_COMMANDS: readonly NocSimCommand[] = ['reset', 'clearCache'];

export interface NocSimCommandRequest {
  stationId: string;
  connectorId?: number;
  command: NocSimCommand;
  payload?: Record<string, unknown>;
}

/** Confirmación del core por cada acción enviada (Message API). */
export interface NocCoreMessageConfirmation {
  success: boolean;
  payload?: unknown;
}

export interface NocSimCommandResult {
  /** Identificador de la petición downlink generado por el backend (traza `command.*`). */
  requestId: string;
  stationId: string;
  protocol: string;
  command: NocSimCommand;
  core: { version: '1.6' | '2.0.1' | '2.1'; module: string; action: string };
  /**
   * `correlationId` OCPP real devuelto por el core, si lo trae. **Nunca** es el
   * `requestId`; si el core no lo informa, queda `null`.
   */
  correlationId: string | null;
  confirmations: NocCoreMessageConfirmation[];
}

export interface NocSimInjectRequest {
  stationId: string;
  action: string;
  payload?: Record<string, unknown>;
}

export interface NocSimInjectResult {
  stationId: string;
  action: string;
  /** Respuesta cruda del API admin del VCP (`/execute`). */
  response: string;
}

/** Conexión efímera de un cargador simulador (Laboratorio EVSE, F7 TG-129). */
export interface NocSimConnectRequest {
  stationId: string;
}

export type NocSimConnectOutcome = 'accepted' | 'rejected' | 'timeout' | 'unavailable';

export interface NocSimConnectResult {
  stationId: string;
  outcome: NocSimConnectOutcome;
  /** `true` si el CP_ID existe en el registro de cargadores. */
  registered: boolean;
  /** Tenant del core con el que se intentó el alta. */
  tenantId: number;
  /** Subprotocolo OCPP usado en el upgrade. */
  protocol: string;
  /** Status devuelto por el core en el CALLRESULT del BootNotification (p. ej. Accepted). */
  bootStatus?: string;
  /** Intervalo de heartbeat en segundos (solo si el core lo informa). */
  interval?: number;
  /** Endpoint WS exacto: `<base>/t{tenantId}/{stationId}`. */
  targetUrl?: string;
  /** Detalle legible (error TLS/red, status HTTP, CALLERROR). */
  detail?: string;
  elapsedMs: number;
}

/** Escenario del catálogo (`GET /api/v1/noc/sim/catalog`). */
export interface NocSimScenarioStep {
  type: 'inject' | 'command';
  label: string;
  stationId: string;
  action?: string;
  payload?: Record<string, unknown>;
  command?: NocSimCommand;
  connectorId?: number;
  waitMs?: number;
}

export interface NocSimScenario {
  id: string;
  name: string;
  description: string;
  stationIds: string[];
  steps: NocSimScenarioStep[];
}

export interface NocSimCatalogResponse {
  scenarios: NocSimScenario[];
}

/** Allowlists vigentes del laboratorio (fuente única: el backend). */
export interface NocSimConfigResponse {
  injectionAllowlist: string[];
}

export interface NocSimScenarioRequest {
  scenarioId: string;
}

export interface NocSimScenarioRunSummary {
  runId: string;
  scenarioId: string;
  status: 'STARTED';
  steps: number;
  startedAt: string;
}

export interface NocSimScenarioRun {
  runId: string;
  scenarioId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  step: number;
  totalSteps: number;
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
}
